import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/Sidebar";
import ChatView from "@/components/ChatView";
import FeedView from "@/components/FeedView";
import CommunityView from "@/components/CommunityView";
import RightPanel from "@/components/RightPanel";
import BottomNav from "@/components/BottomNav";
import HistorySheet from "@/components/HistorySheet";
import { UpgradeModal } from "@/components/UpgradeModal";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import { Menu, PanelRight } from "lucide-react";

export default function Dashboard() {
  const { user, logout, isPro, usage, refreshUsage } = useAuth();
  const [view, setView] = useState("chat");
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [mobileNav, setMobileNav] = useState(false);
  const [mobileRight, setMobileRight] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("history") === "1") {
      setHistoryOpen(true);
      searchParams.delete("history");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadConversations = useCallback(() => {
    api.get("/conversations").then((r) => setConversations(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadConversations(); }, [loadConversations]);

  const openUpgrade = (reason = "Upgrade to Pro for unlimited chat, live web search, and full history.") => {
    setUpgradeReason(reason);
    setUpgradeOpen(true);
  };

  const switchView = (v) => { setView(v); setMobileNav(false); };
  const newChat = () => { setActiveId(null); setView("chat"); setMobileNav(false); };
  const selectConv = (id) => { setActiveId(id); setView("chat"); setMobileNav(false); setHistoryOpen(false); };
  const deleteConv = async (id) => {
    await api.delete(`/conversations/${id}`);
    setConversations((p) => p.filter((c) => c.id !== id));
    if (activeId === id) setActiveId(null);
  };
  const onConversationCreated = (id) => { setActiveId(id); loadConversations(); };

  const manageBilling = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open billing portal.");
    }
  };

  const sidebarProps = {
    view, conversations, activeId, onSelect: selectConv, onNew: newChat, onDelete: deleteConv,
    isPro, isOwner: user?.role === "owner", proExpiresAt: user?.pro_expires_at,
    usage, onUpgrade: () => openUpgrade(), onManageBilling: manageBilling,
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-[#0a0a0f] overflow-hidden">
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} reason={upgradeReason} />

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Desktop left sidebar */}
        <aside className="hidden lg:block w-72 border-r border-[#2a3040] shrink-0">
          <Sidebar {...sidebarProps} />
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile header: history + tools */}
          <header className="lg:hidden sticky top-0 z-20 border-b border-[#2a3040] backdrop-blur-xl bg-[#0a0a0f]/80"
            style={{ paddingTop: "env(safe-area-inset-top)" }}>
            <div className="flex items-center justify-between px-4 h-14">
            <Sheet open={mobileNav} onOpenChange={setMobileNav}>
              <SheetTrigger asChild>
                <button data-testid="open-sidebar-button" className="text-white p-1"><Menu size={22} /></button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-[#0a0a0f] border-[#2a3040]">
                <Sidebar {...sidebarProps} />
              </SheetContent>
            </Sheet>
            <Logo size="text-xl" />
            <div className="flex items-center gap-1">
            <NotificationBell align="end" />
            <Sheet open={mobileRight} onOpenChange={setMobileRight}>
              <SheetTrigger asChild>
                <button data-testid="open-tools-button" className="text-white p-1"><PanelRight size={22} /></button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 p-0 bg-[#0a0a0f] border-[#2a3040] overflow-y-auto">
                <RightPanel />
              </SheetContent>
            </Sheet>
            </div>
            </div>
          </header>

          <main className="flex-1 min-h-0">
            {view === "chat" ? (
              <ChatView conversationId={activeId} isPro={isPro} usage={usage} onNewChat={newChat}
                onConversationCreated={onConversationCreated} onTitleMaybeChanged={loadConversations}
                onUsageChanged={refreshUsage} onLimitReached={openUpgrade} />
            ) : view === "community" ? (
              <CommunityView onUpgrade={openUpgrade} />
            ) : (
              <FeedView />
            )}
          </main>
        </div>

        {/* Desktop right panel */}
        <aside className="hidden xl:block w-80 border-l border-[#2a3040] shrink-0">
          <RightPanel />
        </aside>
      </div>

      {/* Full-width bottom navigation */}
      <BottomNav view={view} setView={switchView} isPro={isPro} user={user} logout={logout}
        onUpgrade={() => openUpgrade()} onManageBilling={manageBilling} onHistory={() => setHistoryOpen(true)} />

      <HistorySheet open={historyOpen} onOpenChange={setHistoryOpen} conversations={conversations}
        activeId={activeId} onSelect={selectConv} onDelete={deleteConv} isPro={isPro}
        onUpgrade={() => { setHistoryOpen(false); openUpgrade(); }} />
    </div>
  );
}
